/** MeetingPrepTab — 「会前准备」页签：客户简报、备忘要点、会前清单。 */

import { Alert, List, Space, Tag, Typography } from "antd";
import type { MeetingAnalysisState, MeetingPrepPayload } from "../../types/meeting";
import EmptyState from "../common/EmptyState";
import MeetingAiActionStatus from "./MeetingAiActionStatus";

const { Paragraph, Text, Title } = Typography;

const TASK_PRIORITY_TAG: Record<string, { label: string; color: string }> = {
  urgent: { label: "紧急", color: "red" },
  high: { label: "重要", color: "orange" },
  normal: { label: "常规", color: "blue" },
  low: { label: "暂缓", color: "default" },
};

interface MeetingPrepTabProps {
  prep: MeetingPrepPayload | null;
  prepPrompted: boolean;
  meetingEnded: boolean;
  state: MeetingAnalysisState;
  errorMessage: string | null;
  completedAt: string | null;
  submitting: boolean;
  onSubmit: () => void;
}

export default function MeetingPrepTab({
  prep,
  prepPrompted,
  meetingEnded,
  state,
  errorMessage,
  completedAt,
  submitting,
  onSubmit,
}: MeetingPrepTabProps) {
  return (
    <div>
      {prepPrompted && (state === "none" || state === "failed") && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: "var(--jerry-sp-3)" }}
          data-testid="meeting-detail-prep-prompt"
          message="这场会可以先准备"
          description="会议即将开始，会前准备还没有生成。"
        />
      )}
      <MeetingAiActionStatus
        action="prepare"
        state={state}
        errorMessage={errorMessage}
        completedAt={completedAt}
        hasPreviousResult={!!prep}
        onSubmit={onSubmit}
        submitting={submitting}
      />
      {meetingEnded && (
        <Paragraph type="secondary">会议已结束，会前准备的内容仅供回顾参考。</Paragraph>
      )}
      {prep ? (
        <div>
          {prep.overallStrategy && (
            <Paragraph
              style={{
                background: "var(--jerry-gray-100)",
                padding: "var(--jerry-sp-3)",
                borderRadius: 6,
                borderLeft: "3px solid var(--jerry-accent-primary)",
              }}
            >
              <Text strong>整体策略：</Text>
              {prep.overallStrategy}
            </Paragraph>
          )}
          <Title level={5}>客户简报</Title>
          <Paragraph
            style={{
              whiteSpace: "pre-wrap",
              background: "var(--jerry-gray-100)",
              padding: "var(--jerry-sp-3)",
              borderRadius: 6,
            }}
          >
            {prep.clientBrief}
          </Paragraph>
          {prep.memoPoints.length > 0 && (
            <>
              <Title level={5}>备忘要点</Title>
              <List
                size="small"
                dataSource={prep.memoPoints}
                renderItem={(m, idx) => (
                  <List.Item key={idx}>
                    <Text>
                      <Text strong>{idx + 1}.</Text> {m}
                    </Text>
                  </List.Item>
                )}
              />
            </>
          )}
          {prep.checklist.length > 0 && (
            <>
              <Title level={5}>会前清单</Title>
              <List
                itemLayout="vertical"
                dataSource={prep.checklist}
                renderItem={(c, idx) => {
                  const pt = TASK_PRIORITY_TAG[c.priority] ?? TASK_PRIORITY_TAG.normal;
                  return (
                    <List.Item key={idx}>
                      <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                        <Space wrap>
                          <Tag color={pt.color}>{pt.label}</Tag>
                          <Text strong>{c.item}</Text>
                        </Space>
                        {c.rationale && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            理由：{c.rationale}
                          </Text>
                        )}
                      </Space>
                    </List.Item>
                  );
                }}
              />
            </>
          )}
        </div>
      ) : (
        <EmptyState
          title="尚未生成会前准备"
          description="点击「生成会前准备」，系统会汇总客户情况、事项进展与最近沟通，给出客户简报、备忘要点与会前清单。"
        />
      )}
    </div>
  );
}
